<template>
  <div class="pdv-movimentacao-page">
    <!-- QR Code search -->
    <div class="qr-search-section">
      <h3>{{ t('Movimentacao por QR Code') }}</h3>
      <div class="qr-search-row">
        <input-text
          v-model="qrCodeInput"
          :label="t('QR Code')"
          :placeholder="t('Escanear ou digitar o QR Code do item')"
          elem-id="pdv_movimentacao_qr_code"
          data-testid="pdv-movimentacao-qr-input"
          @keyup.enter="handleSearch"
        />
        <Button
          :label="t('Buscar')"
          icon="pi pi-search"
          size="small"
          :disabled="!qrCodeInput || isSearching"
          data-testid="pdv-movimentacao-buscar-btn"
          @click="handleSearch"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isSearching" class="loading-state">
      <i class="pi pi-spinner pi-spin"></i>
      <span>{{ t('Buscando...') }}</span>
    </div>

    <!-- Error state -->
    <div v-else-if="searchError" class="error-state">
      <i class="pi pi-exclamation-triangle"></i>
      <p>{{ searchError }}</p>
      <Button :label="t('Tentar novamente')" icon="pi pi-refresh" size="small" severity="secondary" data-testid="pdv-movimentacao-retry-btn" @click="handleSearch" />
    </div>

    <!-- Result: item details + steps -->
    <div v-else-if="lookupResult" class="result-section">
      <!-- Item info header -->
      <div class="item-info-header">
        <div class="item-main-info">
          <h4>{{ lookupResult.item?.description || t('Item') }}</h4>
          <div class="item-details">
            <span v-if="lookupResult.item?.qr_code" class="info-chip">
              <i class="pi pi-qrcode"></i> {{ lookupResult.item.qr_code }}
            </span>
            <span v-if="lookupResult.item?.brand" class="info-chip">
              <i class="pi pi-tag"></i> {{ lookupResult.item.brand }}
            </span>
            <span v-if="lookupResult.item?.model" class="info-chip">
              <i class="pi pi-box"></i> {{ lookupResult.item.model }}
            </span>
          </div>
        </div>
        <div class="order-summary" v-if="lookupResult.item">
          <div class="summary-row">
            <span class="summary-label">{{ t('OS') }}</span>
            <span class="summary-value">#{{ lookupResult.item.order_id }}</span>
          </div>
          <div class="summary-row" v-if="lookupResult.item.client_name">
            <span class="summary-label">{{ t('Cliente') }}</span>
            <span class="summary-value">{{ lookupResult.item.client_name }}</span>
          </div>
        </div>
      </div>

      <!-- Steps timeline -->
      <div class="steps-timeline" v-if="lookupResult.steps && lookupResult.steps.length > 0">
        <h4>{{ t('Histórico de Etapas') }}</h4>
        <div class="timeline">
          <div
            v-for="(step, index) in lookupResult.steps"
            :key="step.id || index"
            class="timeline-item"
            :class="'timeline-' + (step.status || 'pending')"
          >
            <div class="timeline-indicator">
              <i :class="stepIcon(step.status)"></i>
            </div>
            <div class="timeline-content">
              <div class="timeline-header">
                <span class="step-name">{{ step.name || step.step_name || t('Etapa') }} {{ index + 1 }}</span>
                <span class="step-status-label" :class="'status-' + (step.status || 'pending')">
                  {{ stepStatusLabel(step.status) }}
                </span>
              </div>
              <div class="timeline-meta" v-if="step.started_at || step.completed_at">
                <span v-if="step.started_at">{{ t('Início') }}: {{ formatTimestamp(step.started_at) }}</span>
                <span v-if="step.completed_at">{{ t('Conclusão') }}: {{ formatTimestamp(step.completed_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="action-buttons">
        <Button
          v-if="currentStep && currentStep.status === 'pending'"
          :label="t('Iniciar Proxima Etapa')"
          icon="pi pi-play"
          severity="info"
          data-testid="pdv-movimentacao-start-step-btn"
          @click="handleStartCurrentStep"
        />
        <Button
          v-if="currentStep && currentStep.status === 'in_progress'"
          :label="t('Concluir Etapa Atual')"
          icon="pi pi-check"
          severity="success"
          data-testid="pdv-movimentacao-complete-step-btn"
          @click="handleCompleteCurrentStep"
        />
        <Button
          :label="t('Voltar')"
          icon="pi pi-arrow-left"
          severity="secondary"
          text
          data-testid="pdv-movimentacao-back-btn"
          @click="handleBack"
        />
      </div>
    </div>

    <!-- Initial state: no search yet -->
    <div v-else class="initial-state">
      <i class="pi pi-qrcode"></i>
      <p>{{ t('Escaneie ou digite um QR Code para visualizar a movimentacao do item') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { eventBus } from '../../eventBus';
import { useSOProducao } from '../../composables/useSOProducao';
import { log, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface LookupItem {
  description?: string;
  qr_code?: string;
  brand?: string;
  model?: string;
  order_id?: Id;
  client_name?: string;
  [key: string]: unknown;
}

interface LookupStep {
  id?: Id;
  step_id?: Id;
  name?: string;
  step_name?: string;
  status?: string;
  started_at?: string;
  completed_at?: string;
  [key: string]: unknown;
}

interface LookupResult {
  item?: LookupItem;
  steps?: LookupStep[];
  [key: string]: unknown;
}

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { lookupByQrcode, updateStepStatus } = useSOProducao();

const qrCodeInput = ref<string>('');
const isSearching = ref<boolean>(false);
const searchError = ref<string | null>(null);
const lookupResult = ref<LookupResult | null>(null);

const currentStep = computed<LookupStep | null>(() => {
  if (!lookupResult.value?.steps) return null;
  return lookupResult.value.steps.find((s: LookupStep) => s.status !== 'completed' && s.status !== 'skipped') || null;
});

const stepIcon = (status?: string): string => {
  const icons: Record<string, string> = {
    completed: 'pi pi-check-circle',
    in_progress: 'pi pi-spin pi-spinner',
    skipped: 'pi pi-minus-circle',
    pending: 'pi pi-circle'
  };
  return (status && icons[status]) || 'pi pi-circle';
};

const stepStatusLabel = (status?: string): string => {
  const labels: Record<string, string> = {
    completed: t('Concluida'),
    in_progress: t('Em andamento'),
    skipped: t('Pulada'),
    pending: t('Pendente')
  };
  return (status && labels[status]) || status || t('Pendente');
};

const formatTimestamp = (timestamp?: string): string => {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const handleSearch = async (): Promise<void> => {
  if (!qrCodeInput.value) return;

  log('PDVMovimentacaoPage', 'handleSearch', 'Searching QR code', qrCodeInput.value);
  isSearching.value = true;
  searchError.value = null;
  lookupResult.value = null;

  try {
    const result = await lookupByQrcode(qrCodeInput.value) as unknown as LookupResult;
    lookupResult.value = result;
    log('PDVMovimentacaoPage', 'handleSearch', 'Lookup result', result);
  } catch (err) {
    logError('PDVMovimentacaoPage', 'handleSearch', 'Error', err);
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    searchError.value = message || t('QR Code nao encontrado');
  } finally {
    isSearching.value = false;
  }
};

const handleStartCurrentStep = async (): Promise<void> => {
  if (!currentStep.value) return;
  const stepId = currentStep.value.id || currentStep.value.step_id;
  log('PDVMovimentacaoPage', 'handleStartCurrentStep', 'Starting step', { stepId });

  try {
    await updateStepStatus(Number(stepId), 'in_progress');
    eventBus.emit('globalMessage.success', t('Etapa iniciada'));
    await handleSearch();
  } catch (err) {
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    eventBus.emit('globalMessage.error', message || t('Erro ao iniciar etapa'));
  }
};

const handleCompleteCurrentStep = async (): Promise<void> => {
  if (!currentStep.value) return;
  const stepId = currentStep.value.id || currentStep.value.step_id;
  log('PDVMovimentacaoPage', 'handleCompleteCurrentStep', 'Completing step', { stepId });

  try {
    await updateStepStatus(Number(stepId), 'completed');
    eventBus.emit('globalMessage.success', t('Etapa concluida'));
    eventBus.emit('pdv.producao.refresh');
    await handleSearch();
  } catch (err) {
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    eventBus.emit('globalMessage.error', message || t('Erro ao concluir etapa'));
  }
};

const handleBack = (): void => {
  router.push({ name: 'PDVProducao' });
};

onMounted(() => {
  const qrFromQuery = route.query.qrcode;
  if (qrFromQuery && typeof qrFromQuery === 'string') {
    qrCodeInput.value = qrFromQuery;
    handleSearch();
  }
});
</script>

<style scoped>
.pdv-movimentacao-page {
  padding: 0;
  max-width: 900px;
}

.qr-search-section {
  margin-bottom: 24px;
}

.qr-search-section h3 {
  margin: 0 0 12px 0;
}

.qr-search-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.qr-search-row .input-text-wrapper {
  flex: 1;
}

.loading-state,
.error-state,
.initial-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--p-text-muted-color);
}

.loading-state i,
.error-state i,
.initial-state i {
  font-size: 3rem;
  display: block;
  margin-bottom: 12px;
}

.error-state {
  color: #dc3545;
}

.error-state i {
  color: #dc3545;
}

.item-info-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-200);
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
}

.item-main-info h4 {
  margin: 0 0 8px 0;
}

.item-details {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.info-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: var(--p-surface-100);
  border-radius: 12px;
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
}

.order-summary {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-row {
  display: flex;
  gap: 8px;
  font-size: 0.9rem;
}

.summary-label {
  color: var(--p-text-muted-color);
  font-weight: 500;
}

.summary-value {
  font-weight: 600;
}

.steps-timeline {
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-200);
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 20px;
}

.steps-timeline h4 {
  margin: 0 0 16px 0;
}

.timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
}

.timeline-item {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  position: relative;
}

.timeline-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 11px;
  top: 36px;
  bottom: 0;
  width: 2px;
  background: var(--p-surface-200);
}

.timeline-completed:not(:last-child)::after {
  background: #28a745;
}

.timeline-in_progress:not(:last-child)::after {
  background: #007bff;
}

.timeline-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.timeline-indicator i {
  font-size: 1.2rem;
}

.timeline-completed .timeline-indicator i {
  color: #28a745;
}

.timeline-in_progress .timeline-indicator i {
  color: #007bff;
}

.timeline-skipped .timeline-indicator i {
  color: #ffc107;
}

.timeline-pending .timeline-indicator i {
  color: var(--p-surface-400);
}

.timeline-content {
  flex: 1;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.step-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.step-status-label {
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.status-completed {
  background: #d4edda;
  color: #155724;
}

.status-in_progress {
  background: #cce5ff;
  color: #004085;
}

.status-skipped {
  background: #fef3cd;
  color: #856404;
}

.status-pending {
  background: #e2e3e5;
  color: #383d41;
}

.timeline-meta {
  display: flex;
  gap: 16px;
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
  margin-top: 4px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 20px 0;
}

@media (max-width: 768px) {
  .qr-search-row {
    flex-direction: column;
    align-items: stretch;
  }

  .item-info-header {
    flex-direction: column;
  }

  .timeline-meta {
    flex-direction: column;
    gap: 2px;
  }

  .action-buttons {
    flex-direction: column;
  }
}
</style>
